/**
 * PDF template version modal — upload, validation report, activate.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../../config';
import { formatInstantAsJst } from '../../../../../utils/japanTime';
import type { FormTemplateVersion } from './formTemplateTypes';
import '../variousCopiesUpload/VariousCopiesUpload.css';

export interface FormTemplateModalProps {
  isOpen: boolean;
  formCode: string;
  formNameJa: string;
  onClose: () => void;
}

function statusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return '有効';
    case 'VALIDATED':
      return '検証済み（未適用）';
    case 'FAILED':
      return '検証失敗';
    case 'PENDING':
      return '検証中';
    case 'SUPERSEDED':
      return '旧バージョン';
    default:
      return status;
  }
}

function resultLabel(result: string | undefined): string {
  switch (result) {
    case 'passed':
      return '問題なし';
    case 'passed_with_warnings':
      return '警告あり（有効化可能）';
    case 'failed':
      return 'エラーあり（有効化不可）';
    default:
      return result ?? '—';
  }
}

const FormTemplateModal: React.FC<FormTemplateModalProps> = ({
  isOpen,
  formCode,
  formNameJa,
  onClose,
}) => {
  const [versions, setVersions] = useState<FormTemplateVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [latestUpload, setLatestUpload] = useState<FormTemplateVersion | null>(null);

  const loadVersions = useCallback(async () => {
    if (!API_BASE_URL || !formCode) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/forms-to-fill/${encodeURIComponent(formCode)}/template-versions`
      );
      if (!res.ok) throw new Error('バージョン一覧の取得に失敗しました');
      const data: FormTemplateVersion[] = await res.json();
      setVersions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [formCode]);

  useEffect(() => {
    if (isOpen) {
      setLatestUpload(null);
      setSelectedFile(null);
      void loadVersions();
    }
  }, [isOpen, loadVersions]);

  const handleUpload = async () => {
    if (!API_BASE_URL || !selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await fetch(
        `${API_BASE_URL}/api/forms-to-fill/${encodeURIComponent(formCode)}/template-versions`,
        { method: 'POST', body: formData }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          typeof body?.message === 'string' ? body.message : 'アップロードに失敗しました'
        );
      }
      const created: FormTemplateVersion = await res.json();
      setLatestUpload(created);
      setSelectedFile(null);
      await loadVersions();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleActivate = async (versionNumber: number) => {
    if (!API_BASE_URL) return;
    if (!window.confirm(`バージョン v${String(versionNumber).padStart(3, '0')} を有効化しますか？`)) {
      return;
    }
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/forms-to-fill/${encodeURIComponent(formCode)}/template-versions/${versionNumber}/activate`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          typeof body?.message === 'string' ? body.message : '有効化に失敗しました'
        );
      }
      setLatestUpload(null);
      await loadVersions();
    } catch (e) {
      setError(e instanceof Error ? e.message : '有効化に失敗しました');
    }
  };

  const handleDiscard = async (versionNumber: number) => {
    if (!API_BASE_URL) return;
    if (!window.confirm('この下書きバージョンを削除しますか？')) return;
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/forms-to-fill/${encodeURIComponent(formCode)}/template-versions/${versionNumber}`,
        { method: 'DELETE' }
      );
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          typeof body?.message === 'string' ? body.message : '削除に失敗しました'
        );
      }
      if (latestUpload?.versionNumber === versionNumber) setLatestUpload(null);
      await loadVersions();
    } catch (e) {
      setError(e instanceof Error ? e.message : '削除に失敗しました');
    }
  };

  const renderValidationReport = (version: FormTemplateVersion) => {
    const report = version.validation;
    if (!report) return null;
    return (
      <div className="ftf-template-report">
        <p className={`ftf-template-report__result ftf-template-report__result--${report.result}`}>
          検証結果: {resultLabel(report.result)}
        </p>
        <p className="ftf-template-report__summary">
          必須フィールド: {report.presentRequiredCount}/{report.requiredCount}
          {report.missingCount > 0 ? `（不足 ${report.missingCount}）` : ''}
          {report.addedCount > 0 ? ` / 追加 ${report.addedCount}` : ''}
          {report.removedCount > 0 ? ` / 削除 ${report.removedCount}` : ''}
        </p>
        {report.errors.length > 0 && (
          <div className="ftf-template-report__section">
            <strong>エラー</strong>
            <ul>
              {report.errors.map((issue, i) => (
                <li key={`e-${i}`}>
                  {issue.field ? `${issue.field}: ` : ''}
                  {issue.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        {report.warnings.length > 0 && (
          <div className="ftf-template-report__section">
            <strong>警告</strong>
            <ul>
              {report.warnings.map((issue, i) => (
                <li key={`w-${i}`}>
                  {issue.field ? `${issue.field}: ` : ''}
                  {issue.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const activeVersion = versions.find((v) => v.status === 'ACTIVE');

  return (
    <div
      className="vc-upload-modal-overlay"
      onClick={uploading ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ftf-template-modal-title"
    >
      <div className="vc-upload-modal vc-upload-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="vc-upload-modal__header">
          <h2 id="ftf-template-modal-title" className="vc-upload-modal__title">
            PDFテンプレート — {formNameJa}（{formCode}）
          </h2>
          <button
            type="button"
            className="vc-upload-modal__close"
            onClick={onClose}
            aria-label="閉じる"
            disabled={uploading}
          >
            ×
          </button>
        </div>

        <div className="vc-upload-modal__body">
          <p className="ftf-template-intro">
            新しいPDFをアップロードすると、必須AcroFormフィールドの互換性を自動検証します。
            検証に合格したバージョンのみ有効化でき、有効化後に従業員・管理者のプレビューに反映されます。
          </p>

          {activeVersion && (
            <p className="ftf-template-active">
              現在有効: v{String(activeVersion.versionNumber).padStart(3, '0')}
              {activeVersion.activatedAt
                ? `（${formatInstantAsJst(activeVersion.activatedAt)}）`
                : ''}
            </p>
          )}

          <div className="ftf-template-upload-row">
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              disabled={uploading}
            />
            <button
              type="button"
              className="vc-upload__btn-primary"
              disabled={!selectedFile || uploading}
              onClick={() => void handleUpload()}
            >
              {uploading ? '検証中…' : 'アップロードして検証'}
            </button>
          </div>

          {latestUpload && (
            <div className="ftf-template-latest">
              <h3>直近のアップロード（v{String(latestUpload.versionNumber).padStart(3, '0')}）</h3>
              {renderValidationReport(latestUpload)}
              <div className="ftf-template-actions">
                {latestUpload.status === 'VALIDATED' && (
                  <button
                    type="button"
                    className="vc-upload__btn-primary"
                    onClick={() => void handleActivate(latestUpload.versionNumber)}
                  >
                    このバージョンを有効化
                  </button>
                )}
                {(latestUpload.status === 'FAILED' || latestUpload.status === 'VALIDATED') && (
                  <button
                    type="button"
                    className="vc-upload-modal__btn-cancel"
                    onClick={() => void handleDiscard(latestUpload.versionNumber)}
                  >
                    下書きを削除
                  </button>
                )}
              </div>
            </div>
          )}

          {error && (
            <p className="vc-upload__error" role="alert">
              {error}
            </p>
          )}

          <h3 className="ftf-template-history-title">バージョン履歴</h3>
          {loading ? (
            <p className="vc-upload__loading">読み込み中…</p>
          ) : versions.length === 0 ? (
            <p className="vc-upload__empty">アップロードされたテンプレートはありません（組み込みPDFを使用中）。</p>
          ) : (
            <table className="vc-upload__table ftf-template-table">
              <thead>
                <tr>
                  <th>Ver</th>
                  <th>状態</th>
                  <th>アップロード日時</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.versionNumber}>
                    <td>v{String(v.versionNumber).padStart(3, '0')}</td>
                    <td>{statusLabel(v.status)}</td>
                    <td>{v.uploadedAt ? formatInstantAsJst(v.uploadedAt) : '—'}</td>
                    <td className="vc-upload__cell-actions">
                      {v.status === 'VALIDATED' && (
                        <button
                          type="button"
                          className="vc-upload__btn-link"
                          onClick={() => void handleActivate(v.versionNumber)}
                        >
                          有効化
                        </button>
                      )}
                      {(v.status === 'FAILED' || v.status === 'VALIDATED') && (
                        <button
                          type="button"
                          className="vc-upload__btn-link vc-upload__btn-danger"
                          onClick={() => void handleDiscard(v.versionNumber)}
                        >
                          削除
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="vc-upload-modal__footer">
          <div className="vc-upload-modal__footer-right">
            <button type="button" className="vc-upload-modal__btn-cancel" onClick={onClose}>
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormTemplateModal;
